import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useRouteMatch, NavLink, useHistory } from 'react-router-dom';
import { Icon } from 'mastodon/components/icon';
import { useRef, useCallback } from 'react';

const ColumnLink = ({
  icon,
  activeIcon,
  iconComponent,
  activeIconComponent,
  text,
  to,
  href,
  method,
  badge,
  transparent,
  optional,
  onClick,
  ...other
}) => {
  const match = useRouteMatch(to);
  const history = useHistory();
  const isProcessingRef = useRef(false);
  const lastEventTypeRef = useRef(null);
  const className = classNames('column-link', {
    'column-link--transparent': transparent,
    'column-link--optional': optional,
  });
  const badgeElement =
    typeof badge !== 'undefined' ? (
      href === '/notifications' || to === '/notifications' ? (
        badge
      ) : (
        <span className='column-link__badge'>{badge}</span>
      )
    ) : null;
  const iconElement = (typeof icon === 'string' || iconComponent) ? (
    <Icon id={icon} icon={iconComponent} className='column-link__icon' />
  ) : (
    icon
  );
  const activeIconElement = activeIcon ?? (
    activeIconComponent ? (
      <Icon id={icon} icon={activeIconComponent} className='column-link__icon' />
    ) : (
      iconElement
    )
  );
  const active = match?.isExact;

  const handleClick = useCallback(
    (e) => {
      if (isProcessingRef.current) return;

      isProcessingRef.current = true;

      try {
        e.preventDefault();
        onClick?.();
        if (href) {
          if (method) {
            const form = document.createElement('form');
            form.method = method;
            form.action = href;
            document.body.appendChild(form);
            form.submit();
            document.body.removeChild(form);
          } else {
            window.location.href = href;
          }
        } else if (to) {
          history.replace(to);
        }
      } finally {
        setTimeout(() => {
          isProcessingRef.current = false;
          lastEventTypeRef.current = null;
        }, 100);
      }
    },
    [href, to, method, history, onClick]
  );

  if (href) {
    return (
      <a
        href={href}
        className={className}
        data-method={method}
        style={{ touchAction: 'manipulation', userSelect: 'none' }}
        onClick={handleClick}
        {...other}
      >
        {active ? activeIconElement : iconElement}
        <span>{text}</span>
        {badgeElement}
      </a>
    );
  } else {
    return (
      <NavLink
        to={to}
        className={className}
        exact
        onClick={handleClick}
        style={{ touchAction: 'manipulation', userSelect: 'none' }}
        {...other}
      >
        {active ? activeIconElement : iconElement}
        <span>{text}</span>
        {badgeElement}
      </NavLink>
    );
  }
};

ColumnLink.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  iconComponent: PropTypes.func,
  activeIcon: PropTypes.node,
  activeIconComponent: PropTypes.func,
  text: PropTypes.string,
  to: PropTypes.string,
  href: PropTypes.string,
  method: PropTypes.string,
  badge: PropTypes.node,
  transparent: PropTypes.bool,
  optional: PropTypes.bool,
  onClick: PropTypes.func,
};

export default ColumnLink;
